import { useNavigate } from 'react-router-dom';
import { FilmStrip, CaretRight } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { pressable } from '../../lib/a11y';

const THEMES = [
  {
    id: 'bollywood',
    name: 'Bollywood Royale',
    tagline: 'Warm, cinematic, regal',
    fonts: 'Cinzel + Inter',
    palette: ['#C8102E', '#FFD700', '#fff8dc'],
    desc: 'Deep red & gold over a cinematic backdrop. Serif headings evoke classical Indian cinema posters.',
    path: '/themes/bollywood',
    preview: 'https://images.unsplash.com/photo-1542204165-65bf26472b9b?w=800&q=80',
  },
  {
    id: 'hotstar',
    name: 'OTT Modern',
    tagline: 'Like Hotstar / Netflix',
    fonts: 'Poppins ExtraBold',
    palette: ['#7B2FF7', '#f472b6', '#22d3ee'],
    desc: 'Animated hero with purple→pink→cyan gradient, big modern type, ranked carousels. Mobile-first OTT energy.',
    path: '/themes/hotstar',
    preview: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=800&q=80',
  },
  {
    id: 'festival',
    name: 'Festival Prestige',
    tagline: 'Refined, elegant, exclusive',
    fonts: 'Playfair Display + Inter',
    palette: ['#D4AF37', '#0a1020', '#f5f1e6'],
    desc: 'Champagne gold on midnight blue. Italic serif headings, generous spacing, "invitation only" vibe — Cannes meets Sundance.',
    path: '/themes/festival',
    preview: 'https://images.unsplash.com/photo-1518676590629-3dcba9c5a555?w=800&q=80',
  },
];

const ThemesIndex = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-white">
      <div className="noise-overlay" />
      <nav data-web-chrome="nav" className="relative z-10 max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
        <button onClick={() => navigate('/')} className="flex items-center gap-3" data-testid="themes-home-btn">
          <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
        </button>
        <button
          onClick={() => navigate('/')}
          className="btn-secondary"
          data-testid="themes-current-btn"
        >
          Back to Live Landing
        </button>
      </nav>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-14">
          <h1 className="font-heading text-5xl md:text-6xl font-extrabold tracking-tight mb-4">
            CHOOSE YOUR <span className="text-gold">THEME</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Click any preview to view the full landing page. Tell me which one you want as your live homepage.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {THEMES.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              data-testid={`theme-card-${t.id}`}
              className="bg-card border border-white/5 rounded-lg overflow-hidden hover:border-gold/40 transition-all group cursor-pointer"
              {...pressable(() => navigate(t.path), `Open ${t.name} theme`)}
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={t.preview}
                  alt={t.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 flex gap-1">
                  {t.palette.map((c) => (
                    <div
                      key={c}
                      className="w-7 h-7 rounded-full border-2 border-white/20"
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  ))}
                </div>
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-widest text-gold mb-1">{t.tagline}</div>
                <h3 className="font-heading text-2xl font-bold mb-2">{t.name}</h3>
                <p className="text-sm text-slate-400 mb-4 leading-relaxed">{t.desc}</p>
                <div className="text-xs text-slate-400 mb-4">
                  <span className="text-slate-400">Fonts:</span> {t.fonts}
                </div>
                <button
                  data-testid={`theme-view-${t.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(t.path);
                  }}
                  className="btn-primary w-full flex items-center justify-center gap-2 group-hover:gap-3 transition-all"
                >
                  View Full Page
                  <CaretRight size={16} weight="bold" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-12 pt-8 border-t border-white/5">
          <p className="text-sm text-slate-400 mb-3">
            Like one? Tell me <span className="text-gold font-bold">"go with Bollywood Royale"</span> /{' '}
            <span className="text-gold font-bold">"OTT Modern"</span> /{' '}
            <span className="text-gold font-bold">"Festival Prestige"</span> and I'll make it your live `/` page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThemesIndex;
